/** Kraken TCP stack по умолчанию, когда подмена OSFP выключена и id не задан. */
export const DEFAULT_OSFP_ID = 2;

/** Справочник стандартных TCP/OSFP id → подпись (Kraken). */
export const OS_FINGERPRINT_LABELS = {
  1: 'Iphone (Mac OS X)',
  2: 'Android (Linux:2.6.x)',
  3: 'Linux:2.6.x (Google crawler)',
  4: 'Linux:3.11 and newer',
  5: 'Mac OS X',
  6: 'Windows:7 or 8',
  7: 'Windows:7 (Websense crawler)',
  8: 'Windows:NT kernel 5.x',
  9: 'Windows:XP',
};

/** Для отображения: оставляем только часть до «(», без хвоста в скобках. */
export const stripOsFingerprintParens = (value) => {
  if (value == null || value === '—') return value;
  const str = String(value);
  const open = str.indexOf('(');
  if (open === -1) return str.trim();
  const left = str.slice(0, open).trim();
  return left || '—';
};

const toOsfpInt = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
};

const isOsfpFlagTrue = (value) => value === true || value === 1 || value === '1' || value === 'true';

/** Служебные ключи API modem_osfp — не конкретные отпечатки, в dropdown не показываем. */
export const OSFP_FILTER_SKIP_VALUES = new Set(['Выкл', 'Вкл']);

/** Имена из API, которые не показываем в колонке — подставляем id/дефолт. */
const OSFP_COLUMN_SKIP_NAMES = new Set(['Выкл', 'Вкл', 'По умолчанию', 'Отключен', '—', '']);

/** Вытащить числовой ID отпечатка из значения фильтра/строки отображения. */
export const extractOsfpNumericId = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  let match = raw.match(/^ID:\s*(\d+)$/i);
  if (match) return match[1];
  match = raw.match(/\(ID:\s*(\d+)\)\s*$/i);
  if (match) return match[1];
  match = raw.match(/^(\d+)$/);
  if (match) return match[1];
  return null;
};

const resolveOsfpListItemLabel = (item) => {
  if (!item || typeof item !== 'object') return '';
  const displayName = String(item.display_name ?? '').trim();
  if (displayName) return displayName;
  const name = String(item.name ?? '').trim();
  if (name) return name;
  const osPart = String(item.os ?? '').trim();
  const browserPart = String(item.browser ?? '').trim();
  if (osPart && browserPart) return `${osPart} (${browserPart})`;
  return '';
};

/**
 * ID отпечатка для колонки: явная настройка прокси или отпечаток модема (KrakenServer).
 * Логика совпадает с backend _effective_proxy_osfp_id_for_display.
 */
export const resolveEffectiveOsfpId = (proxy) => {
  if (!proxy || typeof proxy !== 'object') return null;

  const proxyId = toOsfpInt(proxy.modem_osfp_id);
  const modemId = toOsfpInt(proxy.server_osfp_id);

  if (isOsfpFlagTrue(proxy.modem_is_osfp) && proxyId !== null && proxyId > 0) return proxyId;
  if (isOsfpFlagTrue(proxy.server_is_osfp) && modemId !== null && modemId > 0) return modemId;
  if (proxyId !== null && proxyId > 0) return proxyId;
  if (modemId !== null && modemId > 0) return modemId;
  return null;
};

/**
 * ID отпечатка для отображения в колонке (включая Kraken default, id=2).
 */
export const resolveDisplayOsfpId = (proxy) => {
  const effectiveId = resolveEffectiveOsfpId(proxy);
  if (effectiveId !== null) return effectiveId;
  return DEFAULT_OSFP_ID;
};

const resolveOsfpLabelById = (osfpId, osfpList = []) => {
  const listItem = (Array.isArray(osfpList) ? osfpList : []).find(
    (item) => Number(item?.id) === osfpId
  );
  if (listItem) {
    const listLabel = resolveOsfpListItemLabel(listItem);
    if (listLabel) return stripOsFingerprintParens(listLabel);
  }

  const staticLabel = OS_FINGERPRINT_LABELS[osfpId];
  if (staticLabel) return stripOsFingerprintParens(staticLabel);

  return null;
};

/**
 * Значение колонки «Отпечаток ОС»: имя из API (osfp_name) или отпечаток модема прокси.
 * При выключенной подмене OSFP показывается стек Kraken по умолчанию (Android).
 */
export const resolveOsFingerprintColumnDisplay = (proxy, osfpList = []) => {
  const osfpName = proxy?.osfp_name != null ? String(proxy.osfp_name).trim() : '';
  if (osfpName && !OSFP_COLUMN_SKIP_NAMES.has(osfpName)) {
    return stripOsFingerprintParens(osfpName);
  }

  const displayId = resolveDisplayOsfpId(proxy);
  const label = resolveOsfpLabelById(displayId, osfpList);
  if (label) return label;

  return '—';
};

/**
 * Карта osfp_id → человекочитаемое имя для фильтра и dropdown.
 */
export const buildOsfpIdToNameMap = (dataRows = [], osfpList = []) => {
  const map = {};

  const put = (id, name) => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    const trimmed = String(name ?? '').trim();
    if (!trimmed || trimmed === '—' || trimmed === 'Выкл' || trimmed === 'Вкл' || trimmed === 'По умолчанию') {
      return;
    }
    const label = stripOsFingerprintParens(trimmed);
    if (!label || label === '—' || /^ID:\s*\d+$/i.test(label)) return;
    const key = String(numericId);
    if (!map[key]) {
      map[key] = label;
    }
  };

  (Array.isArray(osfpList) ? osfpList : []).forEach((item) => {
    put(item?.id, resolveOsfpListItemLabel(item));
  });

  Object.entries(OS_FINGERPRINT_LABELS).forEach(([id, label]) => {
    put(id, label);
  });

  (Array.isArray(dataRows) ? dataRows : []).forEach((row) => {
    const orig = row?._original;
    if (!orig) return;

    const displayId = resolveDisplayOsfpId(orig);
    const osfpName = orig.osfp_name != null ? String(orig.osfp_name).trim() : '';
    if (osfpName && !OSFP_COLUMN_SKIP_NAMES.has(osfpName)) {
      put(displayId, osfpName);
      return;
    }

    const columnLabel = resolveOsFingerprintColumnDisplay(orig, osfpList);
    if (columnLabel && columnLabel !== '—') {
      put(displayId, columnLabel);
    }
  });

  return map;
};

/**
 * Подпись пункта фильтра по ключу API (modem_osfp). Только имя отпечатка или null.
 */
export const resolveOsFingerprintFilterLabel = (filterKey, idToNameMap = {}, dataRows = []) => {
  const raw = String(filterKey ?? '').trim();
  if (!raw || OSFP_FILTER_SKIP_VALUES.has(raw)) return null;

  const numericId = extractOsfpNumericId(raw);
  if (numericId && idToNameMap[numericId]) {
    return idToNameMap[numericId];
  }

  const rows = Array.isArray(dataRows) ? dataRows : [];
  if (numericId) {
    for (const row of rows) {
      const orig = row?._original;
      if (!orig) continue;
      if (String(resolveEffectiveOsfpId(orig)) !== numericId) continue;
      const osfpName = orig.osfp_name != null ? String(orig.osfp_name).trim() : '';
      if (osfpName) return stripOsFingerprintParens(osfpName);
    }

    const staticLabel = OS_FINGERPRINT_LABELS[Number(numericId)];
    if (staticLabel) return stripOsFingerprintParens(staticLabel);
    return null;
  }

  const stripped = stripOsFingerprintParens(raw);
  if (!stripped || stripped === '—' || OSFP_FILTER_SKIP_VALUES.has(stripped) || stripped === 'По умолчанию') {
    return null;
  }
  if (/^ID:\s*\d+$/i.test(stripped)) return null;
  return stripped;
};

/**
 * Модель фильтра колонки «Отпечаток ОС»: только названия отпечатков (без «По умолчанию» / «ID: N»).
 */
export const buildOsFingerprintFilterModel = (filterOptions, dataRows = [], osfpList = []) => {
  const idToNameMap = buildOsfpIdToNameMap(dataRows, osfpList);
  const rawKeys = new Set();

  const apiOptions = filterOptions?.osFingerprint;
  if (Array.isArray(apiOptions)) {
    apiOptions.forEach((value) => {
      const normalized = String(value ?? '').trim();
      if (!normalized || OSFP_FILTER_SKIP_VALUES.has(normalized)) return;
      if (!extractOsfpNumericId(normalized)) return;
      rawKeys.add(normalized);
    });
  }

  if (Array.isArray(dataRows)) {
    dataRows.forEach((row) => {
      const orig = row?._original;
      if (!orig) return;
      const displayId = resolveDisplayOsfpId(orig);
      rawKeys.add(`ID: ${displayId}`);
    });
  }

  const labelToRaw = new Map();
  const rawToLabel = new Map();

  rawKeys.forEach((rawKey) => {
    const label = resolveOsFingerprintFilterLabel(rawKey, idToNameMap, dataRows);
    if (!label) return;

    rawToLabel.set(rawKey, label);
    if (!labelToRaw.has(label)) {
      labelToRaw.set(label, []);
    }
    const bucket = labelToRaw.get(label);
    if (!bucket.includes(rawKey)) {
      bucket.push(rawKey);
    }
  });

  const options = Array.from(labelToRaw.keys()).sort((a, b) => a.localeCompare(b, 'ru'));
  return { options, labelToRaw, rawToLabel, idToNameMap };
};
